<script>
	import { onMount } from "svelte";
	import { PRConnect } from "@projectroom/ui-lib";
	import { Button, Select } from "../../lib/ui";
	import MiniChart from "../insights/MiniChart.svelte";
	import Callout from "./Callout.svelte";

	/**
	 * [운영 지표] 탭 — 이벤트 운영 보고용 숫자(순 방문자·누적 방문자·재방문자·재방문율·행운뽑기 참여).
	 *
	 * ⭐ 보고서 상자(.opsr-sheet) **한 장만 캡처하면 제출용**이 되게 만든다 — 런처 이름·집계 기간·기준 시각·
	 *    지표 정의가 상자 안에 다 있다. 조회 조건(기간·대상)은 상자 **밖**에 둬서 캡처에 안 섞인다.
	 * ⚠️ 지표 정의는 서버 Bot.getLauncherOpsReport 주석과 같아야 한다(각주가 곧 정의서다).
	 * ⚠️ 방문 기록은 서버가 런처 부팅(Launcher.me) 때 남긴다 — 미리보기는 안 센다. 기록은 이 기능 배포
	 *    뒤부터 쌓인다(trackingSince).
	 * ⚠️ 저장할 것이 없는 탭이다 — 저작 설정을 고치지 않는다(빌더가 이 탭에선 미리보기를 접는다).
	 */
	export let bot;
	export let recipes = [];

	const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
	const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
	const addDays = (day, n) => new Date(Date.parse(`${day}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
	// ⚠️ 하루 경계는 서버와 같은 KST — 기기 시계·시간대를 타지 않게 직접 더한다.
	const todayKst = () => new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

	/** 경품 이벤트 중 '행운뽑기'(즉석 뽑기)만 — 순위 보상(예정)은 참여가 뽑기가 아니다. */
	$: drawEvents = (recipes || []).filter((r) => r && r.kind === "event" && (!r.event?.mode || r.event.mode === "draw"));
	$: eventPresets = drawEvents.filter((r) => DATE_RE.test(r.event?.startDate || ""));
	const eventName = (r) => r?.name || "이름 없는 이벤트";

	// 조회 조건 — 기간 묶음(ev:<id> / 7 / 30 / custom) · 날짜 · 뽑기 대상('' = 모든 경품 이벤트)
	let preset = "";
	let from = "";
	let to = "";
	let recipeFilter = "";

	const applyPreset = (p) => {
		preset = p;
		const today = todayKst();
		if (p.startsWith("ev:")) {
			const r = drawEvents.find((x) => x.id === p.slice(3));
			if (!r) return applyPreset("30");
			from = r.event.startDate;
			to = DATE_RE.test(r.event?.endDate || "") ? r.event.endDate : today;
			recipeFilter = r.id;
		} else if (p === "7" || p === "30") {
			to = today;
			from = addDays(today, -(Number(p) - 1));
		}
	};

	/** 처음 고를 기간: 지금 진행 중인 이벤트 → 기간이 있는 첫 이벤트 → 최근 30일. */
	const initialPreset = () => {
		const today = todayKst();
		const live = eventPresets.find((r) => r.event.startDate <= today && (!DATE_RE.test(r.event.endDate || "") || today <= r.event.endDate));
		const pick = live || eventPresets[0];
		return pick ? `ev:${pick.id}` : "30";
	};

	let report = null;
	let loading = false;
	let error = "";
	let seq = 0;
	const load = () => {
		const mine = ++seq;
		loading = true;
		error = "";
		// ⚠️ PRConnect 실패 콜백은 네트워크 오류에서 안 불린다 → 오래 걸리면 풀어 준다(빌더 저장 큐와 같은 이유).
		const watchdog = setTimeout(() => {
			if (mine !== seq || !loading) return;
			loading = false;
			error = "응답이 없습니다. 잠시 뒤 새로고침해 주세요.";
		}, 20000);
		PRConnect.send(
			"/api/Bot/getLauncherOpsReport",
			{ botId: bot.id, from, to, recipeId: recipeFilter },
			(data) => {
				clearTimeout(watchdog);
				if (mine !== seq) return;
				loading = false;
				if (data.header.status === "success") {
					report = data.result;
					// 서버가 고친 기간(뒤바뀐 날짜·1년 상한)을 화면에도 반영한다
					from = report.from;
					to = report.to;
				} else error = "불러오지 못했습니다. (서버를 재시작했는지, LAUNCHER_VISIT 마이그레이션을 넣었는지 확인해 주세요)";
			},
			() => {
				clearTimeout(watchdog);
				if (mine !== seq) return;
				loading = false;
				error = "불러오지 못했습니다.";
			},
		);
	};

	onMount(() => {
		applyPreset(initialPreset());
		load();
	});

	const onPreset = (e) => {
		applyPreset(e.target.value);
		if (preset !== "custom") load();
	};
	const onDate = (which, e) => {
		const v = e.target.value;
		if (!DATE_RE.test(v)) return;
		if (which === "from") from = v;
		else to = v;
		preset = "custom";
		load();
	};
	const onRecipeFilter = (e) => {
		recipeFilter = e.target.value;
		// 이벤트 기간을 보던 중 대상을 바꾸면 그 기간은 더 이상 '그 이벤트 기간'이 아니다
		if (preset.startsWith("ev:") && preset.slice(3) !== recipeFilter) preset = "custom";
		load();
	};

	/* ── 표시 ── */
	const num = (v) => Number(v || 0).toLocaleString("ko-KR");
	const fmtDay = (d) => {
		if (!DATE_RE.test(d || "")) return "";
		const wd = WEEKDAYS[new Date(`${d}T00:00:00Z`).getUTCDay()];
		return `${d.replace(/-/g, ".")} (${wd})`;
	};
	/** 기준 시각 — 집계 기간과 같은 표기(YYYY.MM.DD HH:MM). KST 는 직접 더한다(기기 시간대를 타지 않게). */
	const fmtStamp = (iso) => {
		const ms = Date.parse(iso || "");
		if (!Number.isFinite(ms)) return "";
		const k = new Date(ms + 9 * 60 * 60 * 1000).toISOString();
		return `${k.slice(0, 10).replace(/-/g, ".")} ${k.slice(11, 16)}`;
	};

	$: t = report?.totals;
	$: days = report?.days || [];
	$: targetRecipe = report?.recipeId ? drawEvents.find((r) => r.id === report.recipeId) : null;
	$: targetLabel = report?.recipeId
		? targetRecipe
			? eventName(targetRecipe)
			: "선택한 이벤트(지워짐)"
		: drawEvents.length
			? "모든 경품 이벤트"
			: "경품 이벤트 없음";
	$: rateText = t && t.uniqueVisitors ? `${t.returningRate.toFixed(1)}` : "-";
	$: visitSeries = days.map((d) => ({ label: d.day.slice(5).replace("-", "."), value: d.visitors }));
	$: drawSeries = days.map((d) => ({ label: d.day.slice(5).replace("-", "."), value: d.draws }));
	$: sum = days.reduce(
		(a, d) => ({
			visitors: a.visitors + d.visitors,
			newVisitors: a.newVisitors + d.newVisitors,
			returningVisitors: a.returningVisitors + d.returningVisitors,
			draws: a.draws + d.draws,
			wins: a.wins + d.wins,
		}),
		{ visitors: 0, newVisitors: 0, returningVisitors: 0, draws: 0, wins: 0 },
	);
	// 고른 기간이 기록 시작일보다 앞이면, 그 앞 날짜는 '0명'이 아니라 '기록 없음'이다 — 각주로 알린다
	$: beforeTracking = report?.trackingSince && report.from < report.trackingSince;
	$: endsInFuture = report && report.to > report.today;

	let showTable = true;

	/** 엑셀·시트에 붙여 넣을 수 있는 탭 구분 글. 보고서 상자와 같은 내용·같은 순서. */
	let copied = false;
	const copyTsv = async () => {
		if (!report) return;
		const lines = [
			`${bot.title || "런처"} 운영 지표`,
			`집계 기간\t${report.from} ~ ${report.to}`,
			`행운뽑기 대상\t${targetLabel}`,
			`기준 시각(KST)\t${fmtStamp(report.generatedAt)}`,
			"",
			`순 방문자 수\t${t.uniqueVisitors}`,
			`누적 방문자 수\t${t.totalVisits}`,
			`재방문자 수\t${t.returningVisitors}`,
			`재방문율(%)\t${t.uniqueVisitors ? t.returningRate.toFixed(1) : "-"}`,
			`행운뽑기 참여건수\t${t.draws}`,
			`행운뽑기 참여 기기\t${t.drawParticipants}`,
			`행운뽑기 당첨\t${t.wins}`,
			"",
			["날짜", "방문자", "신규", "재방문", "행운뽑기 참여", "당첨"].join("\t"),
			...days.map((d) => [d.day, d.visitors, d.newVisitors, d.returningVisitors, d.draws, d.wins].join("\t")),
			["합계", sum.visitors, sum.newVisitors, sum.returningVisitors, sum.draws, sum.wins].join("\t"),
		];
		try {
			await navigator.clipboard.writeText(lines.join("\n"));
			copied = true;
			setTimeout(() => (copied = false), 1500);
		} catch {
			/* 복사가 막힌 환경 — 표를 보고 옮기면 된다 */
		}
	};
</script>

<!-- ── 조회 조건(캡처 범위 밖) ── -->
<div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
	<Select class="tw-w-auto tw-min-w-[200px] tw-max-w-full" value={preset} on:change={onPreset}>
		{#each eventPresets as r (r.id)}
			<option value={`ev:${r.id}`}>이벤트 기간 · {eventName(r)}</option>
		{/each}
		<option value="7">최근 7일</option>
		<option value="30">최근 30일</option>
		<option value="custom">직접 지정</option>
	</Select>
	<div class="tw-flex tw-items-center tw-gap-1.5">
		<input type="date" class="opsr-date" value={from} max={to || undefined} on:change={(e) => onDate("from", e)} aria-label="시작일" />
		<span class="tw-text-sm tw-text-muted-foreground">~</span>
		<input type="date" class="opsr-date" value={to} min={from || undefined} on:change={(e) => onDate("to", e)} aria-label="종료일" />
	</div>
	{#if drawEvents.length > 1}
		<Select class="tw-w-auto tw-min-w-[180px] tw-max-w-full" value={recipeFilter} on:change={onRecipeFilter}>
			<option value="">행운뽑기 · 모든 경품 이벤트</option>
			{#each drawEvents as r (r.id)}
				<option value={r.id}>행운뽑기 · {eventName(r)}</option>
			{/each}
		</Select>
	{/if}
	<div class="tw-ml-auto tw-flex tw-items-center tw-gap-2">
		<Button variant="outline" size="sm" on:click={() => (showTable = !showTable)}>{showTable ? "일별 표 숨기기" : "일별 표 보기"}</Button>
		<Button variant="outline" size="sm" on:click={copyTsv} disabled={!report}>{copied ? "복사됨" : "엑셀용 복사"}</Button>
		<Button variant="outline" size="sm" on:click={load} disabled={loading}>{loading ? "불러오는 중…" : "새로고침"}</Button>
	</div>
</div>
<p class="tw-text-xs tw-text-muted-foreground">
	아래 상자 하나만 캡처하면 제출용 한 장이 됩니다 — 런처 이름·집계 기간·기준 시각·지표 정의가 상자 안에 들어 있습니다.
</p>

{#if error}
	<Callout variant="error">{error}</Callout>
{/if}
{#if report && !report.trackingSince}
	<Callout variant="warn">아직 방문 기록이 없습니다. 게시본(공개 주소)으로 들어온 방문부터 쌓입니다 — 빌더 미리보기는 세지 않습니다.</Callout>
{/if}

{#if report && t}
	<!-- ── 보고서(캡처 범위) ── -->
	<section class="opsr-sheet" aria-label="운영 지표 보고서">
		<header class="opsr-head">
			<div class="tw-min-w-0">
				<div class="opsr-kicker">런처페이지 운영 지표</div>
				<h2 class="opsr-title">{bot.title || "런처"}</h2>
			</div>
			<dl class="opsr-meta">
				<div><dt>집계 기간</dt><dd>{fmtDay(report.from)} ~ {fmtDay(report.to)}</dd></div>
				<div><dt>행운뽑기 대상</dt><dd>{targetLabel}</dd></div>
				<div><dt>기준 시각</dt><dd>{fmtStamp(report.generatedAt)} (KST)</dd></div>
			</dl>
		</header>

		<div class="opsr-kpis">
			<div class="opsr-kpi">
				<div class="opsr-kpi-l">순 방문자 수</div>
				<div class="opsr-kpi-v">{num(t.uniqueVisitors)}<span>명</span></div>
				<div class="opsr-kpi-s">중복 없이 센 방문자</div>
			</div>
			<div class="opsr-kpi">
				<div class="opsr-kpi-l">누적 방문자 수</div>
				<div class="opsr-kpi-v">{num(t.totalVisits)}<span>명</span></div>
				<div class="opsr-kpi-s">날마다 센 방문자의 합</div>
			</div>
			<div class="opsr-kpi">
				<div class="opsr-kpi-l">재방문자 수</div>
				<div class="opsr-kpi-v">{num(t.returningVisitors)}<span>명</span></div>
				<div class="opsr-kpi-s">기간 중 2일 이상 방문</div>
			</div>
			<div class="opsr-kpi">
				<div class="opsr-kpi-l">재방문율</div>
				<div class="opsr-kpi-v">{rateText}{#if rateText !== "-"}<span>%</span>{/if}</div>
				<div class="opsr-kpi-s">재방문자 ÷ 순 방문자</div>
			</div>
			<div class="opsr-kpi">
				<div class="opsr-kpi-l">행운뽑기 참여건수</div>
				<div class="opsr-kpi-v">{num(t.draws)}<span>건</span></div>
				<div class="opsr-kpi-s">참여 기기 {num(t.drawParticipants)}대 · 당첨 {num(t.wins)}건</div>
			</div>
		</div>

		{#if days.length > 0}
			<div class="opsr-charts">
				<MiniChart title="일별 방문자" subtitle="명" kind="bar" series={visitSeries} />
				<MiniChart title="일별 행운뽑기 참여" subtitle="건" kind="bar" series={drawSeries} />
			</div>

			{#if showTable}
				<div class="opsr-table-wrap">
					<table class="opsr-table">
						<thead>
							<tr>
								<th class="opsr-l">날짜</th>
								<th>방문자</th>
								<th>신규</th>
								<th>재방문</th>
								<th>행운뽑기 참여</th>
								<th>당첨</th>
							</tr>
						</thead>
						<tbody>
							{#each days as d (d.day)}
								<tr>
									<td class="opsr-l">{fmtDay(d.day)}</td>
									<td>{num(d.visitors)}</td>
									<td>{num(d.newVisitors)}</td>
									<td>{num(d.returningVisitors)}</td>
									<td>{num(d.draws)}</td>
									<td>{num(d.wins)}</td>
								</tr>
							{/each}
						</tbody>
						<tfoot>
							<tr>
								<td class="opsr-l">합계</td>
								<td>{num(sum.visitors)}</td>
								<td>{num(sum.newVisitors)}</td>
								<td>{num(sum.returningVisitors)}</td>
								<td>{num(sum.draws)}</td>
								<td>{num(sum.wins)}</td>
							</tr>
						</tfoot>
					</table>
				</div>
			{/if}
		{:else}
			<div class="opsr-empty">아직 시작하지 않은 기간입니다 — 시작일부터 날마다 쌓입니다.</div>
		{/if}

		<footer class="opsr-notes">
			<p>· 방문자는 <b>기기(브라우저) 기준, 하루 한 번</b> 셉니다. 같은 기기가 하루에 여러 번 들어와도 1명이며, 로그인 여부와 상관없습니다.</p>
			<p>· 순 방문자 수 = 기간 중 한 번이라도 온 기기 수 · 누적 방문자 수 = 일별 방문자의 합 · 재방문자 = 기간 중 2일 이상 온 기기</p>
			<p>· 일별 '신규'는 이 기간에서 처음 온 기기, '재방문'은 이 기간에 전에 온 적 있는 기기입니다(신규의 합 = 순 방문자 수).</p>
			{#if showTable}
				<p>· 표 합계의 '재방문'은 재방문한 <b>횟수</b>(누적 − 순 방문자)이고, 위 '재방문자 수'는 <b>사람 수</b>라 서로 다를 수 있습니다.</p>
			{/if}
			<p>· 행운뽑기 참여건수 = 뽑기를 누른 횟수(당첨·꽝 모두). 빌더 미리보기에서 본 화면·누른 뽑기는 집계에서 빠집니다.</p>
			{#if beforeTracking}
				<p>· 방문 기록은 {fmtDay(report.trackingSince)}부터 쌓였습니다. 그 전 날짜의 방문자는 0으로 표시됩니다.</p>
			{/if}
			{#if endsInFuture}
				<p>· 진행 중인 기간이라 {fmtDay(report.today)}까지의 집계입니다.</p>
			{/if}
		</footer>
	</section>
{:else if loading}
	<div class="opsr-empty">불러오는 중…</div>
{/if}

<style>
	.opsr-date {
		height: 36px;
		padding: 0 8px;
		border: 1px solid hsl(var(--sb-input));
		border-radius: 6px;
		background: hsl(var(--sb-background));
		color: hsl(var(--sb-foreground));
		font-size: 13px;
		font-variant-numeric: tabular-nums;
	}
	/* 기기 날짜 칸의 달력 아이콘은 color-scheme 을 따른다 — 안 주면 다크에서 검은 아이콘이 묻힌다 */
	:global(.dark) .opsr-date {
		color-scheme: dark;
	}

	.opsr-sheet {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 12px;
		background: hsl(var(--sb-card));
		padding: 22px 22px 16px;
		display: flex;
		flex-direction: column;
		gap: 18px;
	}

	.opsr-head {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		justify-content: space-between;
		gap: 12px 24px;
		padding-bottom: 14px;
		border-bottom: 1px solid hsl(var(--sb-border));
	}
	.opsr-kicker {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
	.opsr-title {
		margin: 2px 0 0;
		font-size: 22px;
		font-weight: 700;
		line-height: 1.25;
		color: hsl(var(--sb-foreground));
		word-break: keep-all;
	}
	.opsr-meta {
		margin: 0;
		display: grid;
		grid-template-columns: auto auto;
		gap: 3px 12px;
		font-size: 12px;
	}
	.opsr-meta > div {
		display: contents;
	}
	.opsr-meta dt {
		color: hsl(var(--sb-muted-foreground));
	}
	.opsr-meta dd {
		margin: 0;
		color: hsl(var(--sb-foreground));
		font-weight: 500;
		font-variant-numeric: tabular-nums;
	}

	.opsr-kpis {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(138px, 1fr));
		gap: 10px;
	}
	.opsr-kpi {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 10px;
		padding: 12px 14px;
		background: hsl(var(--sb-background));
	}
	.opsr-kpi-l {
		font-size: 12px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
	.opsr-kpi-v {
		margin-top: 6px;
		font-size: 28px;
		font-weight: 700;
		line-height: 1.1;
		color: hsl(var(--sb-foreground));
		font-variant-numeric: tabular-nums;
		letter-spacing: -0.01em;
	}
	.opsr-kpi-v span {
		margin-left: 2px;
		font-size: 14px;
		font-weight: 600;
		color: hsl(var(--sb-muted-foreground));
	}
	.opsr-kpi-s {
		margin-top: 6px;
		word-break: keep-all;
		font-size: 11px;
		line-height: 1.4;
		color: hsl(var(--sb-muted-foreground));
	}

	.opsr-charts {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
		gap: 10px;
	}

	.opsr-table-wrap {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 10px;
		overflow: hidden;
	}
	.opsr-table {
		width: 100%;
		border-collapse: collapse;
		font-size: 12px;
		font-variant-numeric: tabular-nums;
		color: hsl(var(--sb-foreground));
	}
	.opsr-table th,
	.opsr-table td {
		padding: 6px 12px;
		text-align: right;
		white-space: nowrap;
	}
	/* ⚠️ tw-text-left 로는 안 된다 — 위 스코프 규칙(.opsr-table td.svelte-xxx)이 특이도로 이긴다 */
	.opsr-table .opsr-l {
		text-align: left;
	}
	.opsr-table thead th {
		background: hsl(var(--sb-muted));
		color: hsl(var(--sb-muted-foreground));
		font-weight: 600;
	}
	.opsr-table tbody tr + tr td {
		border-top: 1px solid hsl(var(--sb-border));
	}
	.opsr-table tfoot td {
		border-top: 2px solid hsl(var(--sb-border));
		font-weight: 700;
		background: hsl(var(--sb-muted) / 0.5);
	}

	.opsr-empty {
		padding: 28px 12px;
		text-align: center;
		font-size: 13px;
		color: hsl(var(--sb-muted-foreground));
	}

	.opsr-notes {
		padding-top: 12px;
		border-top: 1px solid hsl(var(--sb-border));
		font-size: 11px;
		line-height: 1.6;
		color: hsl(var(--sb-muted-foreground));
	}
	.opsr-notes p {
		margin: 0;
	}
	.opsr-notes b {
		color: hsl(var(--sb-foreground));
		font-weight: 600;
	}
</style>
